import {CalendarDays, ChevronLeft, ChevronRight} from 'lucide-react'
import React, {FC, useCallback, useEffect, useId, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

/**
 * Calendar date picker for the onboarding wizard.
 *
 * Replaces the native `<input type='date'>` whose popup is drawn by the OS and
 * cannot be styled — it looked nothing like the wizard's FilterSelect menus.
 * The popover here borrows the FilterSelect menu surface (same border, radius,
 * shadow and bootstrap theme vars, see `_onboardingwizard.scss`) so every
 * dropdown in the wizard reads as one family.
 *
 * Value is an ISO `YYYY-MM-DD` string (or `''`), matching what the native input
 * produced, so callers' min/max/ordering comparisons keep working unchanged.
 *
 * The popover is portalled to `document.body` and positioned `fixed` from the
 * trigger's measured rect: the wizard's edit sheet scrolls and its steps sit in
 * transformed containers, both of which would clip or re-base an in-place
 * popover. Like FilterSelect, scrolling an ancestor closes it rather than
 * letting it drift away from the trigger.
 */

interface Props {
  /** ISO `YYYY-MM-DD`, or `''` for no selection. */
  value: string
  onChange: (iso: string) => void
  /** Inclusive ISO bounds; days outside are disabled and the views cannot leave them. */
  min?: string
  max?: string
  /** Element id for the trigger so a `<label htmlFor>` can target it. */
  id?: string
  /** Extra classes on the trigger (e.g. the sub-step's `step2-duration-input`). */
  className?: string
  /**
   * Inline trigger styles (background/border/color) so a host can match its
   * neighbouring inputs. The default look comes from `.wizard-datepicker-trigger`.
   */
  style?: React.CSSProperties
  /** Already-translated placeholder shown when there is no value. */
  placeholder?: string
  ariaLabel?: string
  disabled?: boolean
}

type Ymd = {y: number; m: number; d: number}
type View = 'days' | 'months' | 'years'

/** Narrowest the popover may get; a 7-column grid needs about this much. */
const POPOVER_MIN_WIDTH = 200
/** Widest it may get; past this the day cells look sparse. */
const POPOVER_MAX_WIDTH = 300
const POPOVER_HEIGHT = 340
const VIEWPORT_MARGIN = 8
const YEARS_PER_PAGE = 12

const pad = (n: number) => String(n).padStart(2, '0')
const toIso = ({y, m, d}: Ymd) => `${String(y).padStart(4, '0')}-${pad(m)}-${pad(d)}`
const parseIso = (iso: string | undefined): Ymd | null => {
  if (!iso) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return null
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  if (m < 1 || m > 12 || d < 1 || d > 31) return null
  return {y, m, d}
}
/** Local-noon Date so DST shifts cannot roll the calendar day. */
const toDate = ({y, m, d}: Ymd) => {
  const date = new Date(2000, m - 1, d, 12)
  date.setFullYear(y) // years < 100 must be set explicitly
  return date
}
const todayYmd = (): Ymd => {
  const now = new Date()
  return {y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate()}
}
const daysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate()
/** Weekday index with Monday = 0 … Sunday = 6. */
const mondayIndex = (y: number, m: number, d: number) => (toDate({y, m, d}).getDay() + 6) % 7

const WizardDatePicker: FC<Props> = ({
  value,
  onChange,
  min,
  max,
  id,
  className = '',
  style,
  placeholder,
  ariaLabel,
  disabled = false,
}) => {
  const intl = useIntl()
  const fallbackId = useId()
  const triggerId = id ?? fallbackId
  const popoverId = `${triggerId}-calendar`

  const selected = parseIso(value)
  const today = todayYmd()

  const [open, setOpen] = useState(false)
  const [view, setView] = useState<View>('days')
  const [cursor, setCursor] = useState<{y: number; m: number}>(() => {
    const base = selected ?? today
    return {y: base.y, m: base.m}
  })
  const [pos, setPos] = useState<React.CSSProperties | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  const isBeforeMin = (iso: string) => !!min && iso < min
  const isAfterMax = (iso: string) => !!max && iso > max
  const isDisabledDay = (ymd: Ymd) => {
    const iso = toIso(ymd)
    return isBeforeMin(iso) || isAfterMax(iso)
  }
  /** A month is reachable if any of its days is inside the bounds. */
  const isMonthReachable = (y: number, m: number) => {
    const first = toIso({y, m, d: 1})
    const last = toIso({y, m, d: daysInMonth(y, m)})
    return !isAfterMax(first) && !isBeforeMin(last)
  }
  const isYearReachable = (y: number) => isMonthReachable(y, 1) || isMonthReachable(y, 12)

  const openPopover = () => {
    if (disabled) return
    const base = selected ?? today
    setCursor({y: base.y, m: base.m})
    setView('days')
    setOpen(true)
  }

  const measure = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect()
    if (!r) return
    const vw = window.innerWidth
    const vh = window.innerHeight
    const spaceBelow = vh - r.bottom - 4 - VIEWPORT_MARGIN
    const spaceAbove = r.top - 4 - VIEWPORT_MARGIN
    const dropUp = spaceBelow < POPOVER_HEIGHT && spaceAbove > spaceBelow
    // Match the trigger's width (within bounds) so a start-date calendar stays
    // under the start field instead of spilling under its end-date neighbour.
    const width = Math.min(
      Math.max(r.width, POPOVER_MIN_WIDTH),
      POPOVER_MAX_WIDTH,
      vw - VIEWPORT_MARGIN * 2
    )
    // When the popover must be wider than the trigger, grow it away from the
    // trigger's neighbour: right-aligned triggers open leftwards, and RTL
    // documents anchor on the right edge.
    const rtl = document.documentElement.dir === 'rtl'
    const anchorRight = rtl || r.left + r.width / 2 > vw / 2
    const rawLeft = anchorRight ? r.right - width : r.left
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rawLeft, vw - width - VIEWPORT_MARGIN))
    setPos({
      ...(dropUp ? {bottom: vh - r.top + 4} : {top: r.bottom + 4}),
      left,
      width,
    })
  }, [])

  useLayoutEffect(() => {
    if (open) measure()
  }, [open, measure])

  useEffect(() => {
    if (!open) return
    const onDocPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    // Capture phase so any scrolling ancestor closes the popover (its position
    // was measured once and would otherwise drift). Scrolling inside the
    // popover itself must not dismiss it.
    const onScroll = (e: Event) => {
      if (popoverRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    const close = () => setOpen(false)
    document.addEventListener('mousedown', onDocPointerDown)
    document.addEventListener('touchstart', onDocPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown)
      document.removeEventListener('touchstart', onDocPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const pick = (ymd: Ymd) => {
    if (isDisabledDay(ymd)) return
    onChange(toIso(ymd))
    setOpen(false)
    triggerRef.current?.focus()
  }

  const shiftMonth = (delta: number) => {
    setCursor((c) => {
      const idx = c.y * 12 + (c.m - 1) + delta
      return {y: Math.floor(idx / 12), m: (idx % 12) + 1}
    })
  }
  const shiftYear = (delta: number) => setCursor((c) => ({...c, y: c.y + delta}))
  const shiftYearPage = (delta: number) =>
    setCursor((c) => ({...c, y: c.y + delta * YEARS_PER_PAGE}))

  const monthLabel = (m: number, form: 'long' | 'short') =>
    intl.formatDate(toDate({y: 2000, m, d: 1}), {month: form})
  const weekdayLabels = Array.from({length: 7}, (_, i) =>
    // 2024-01-01 is a Monday.
    intl.formatDate(new Date(2024, 0, 1 + i, 12), {weekday: 'short'})
  )

  const displayValue = selected
    ? intl.formatDate(toDate(selected), {day: 'numeric', month: 'short', year: 'numeric'})
    : ''

  const prevLabel = intl.formatMessage({id: 'ONBOARDING_WIZARD.DATEPICKER.PREVIOUS'})
  const nextLabel = intl.formatMessage({id: 'ONBOARDING_WIZARD.DATEPICKER.NEXT'})

  /* ------------------------------- views ---------------------------------- */

  const renderDays = () => {
    const {y, m} = cursor
    const lead = mondayIndex(y, m, 1)
    const count = daysInMonth(y, m)
    const cells: (Ymd | null)[] = [
      ...Array.from({length: lead}, () => null),
      ...Array.from({length: count}, (_, i) => ({y, m, d: i + 1})),
    ]
    while (cells.length % 7 !== 0) cells.push(null)
    const selectedIso = selected ? toIso(selected) : ''
    const todayIso = toIso(today)
    return (
      <>
        <div className='wizard-datepicker-weekdays' aria-hidden>
          {weekdayLabels.map((w) => (
            <span key={w}>{w}</span>
          ))}
        </div>
        <div className='wizard-datepicker-grid' role='grid'>
          {cells.map((cell, i) => {
            if (!cell) return <span key={`empty-${i}`} />
            const iso = toIso(cell)
            const isSelected = iso === selectedIso
            const isToday = iso === todayIso
            return (
              <button
                key={iso}
                type='button'
                role='gridcell'
                aria-selected={isSelected}
                aria-label={intl.formatDate(toDate(cell), {dateStyle: 'full'})}
                disabled={isDisabledDay(cell)}
                className={`wizard-datepicker-day${isSelected ? ' is-selected' : ''}${isToday ? ' is-today' : ''}`}
                onClick={() => pick(cell)}
              >
                {cell.d}
              </button>
            )
          })}
        </div>
      </>
    )
  }

  const renderMonths = () => (
    <div className='wizard-datepicker-grid wizard-datepicker-grid--3'>
      {Array.from({length: 12}, (_, i) => i + 1).map((m) => {
        const isCurrent = selected?.y === cursor.y && selected?.m === m
        return (
          <button
            key={m}
            type='button'
            disabled={!isMonthReachable(cursor.y, m)}
            className={`wizard-datepicker-cell${isCurrent ? ' is-selected' : ''}`}
            onClick={() => {
              setCursor((c) => ({...c, m}))
              setView('days')
            }}
          >
            {monthLabel(m, 'short')}
          </button>
        )
      })}
    </div>
  )

  const renderYears = () => {
    const start = Math.floor(cursor.y / YEARS_PER_PAGE) * YEARS_PER_PAGE
    return (
      <div className='wizard-datepicker-grid wizard-datepicker-grid--3'>
        {Array.from({length: YEARS_PER_PAGE}, (_, i) => start + i).map((y) => (
          <button
            key={y}
            type='button'
            disabled={y < 1 || !isYearReachable(y)}
            className={`wizard-datepicker-cell${selected?.y === y ? ' is-selected' : ''}`}
            onClick={() => {
              setCursor((c) => ({...c, y}))
              setView('months')
            }}
          >
            {y}
          </button>
        ))}
      </div>
    )
  }

  const headerLabel =
    view === 'days'
      ? `${monthLabel(cursor.m, 'long')} ${cursor.y}`
      : view === 'months'
        ? String(cursor.y)
        : (() => {
            const start = Math.floor(cursor.y / YEARS_PER_PAGE) * YEARS_PER_PAGE
            return `${start} – ${start + YEARS_PER_PAGE - 1}`
          })()

  const onPrev = () =>
    view === 'days' ? shiftMonth(-1) : view === 'months' ? shiftYear(-1) : shiftYearPage(-1)
  const onNext = () =>
    view === 'days' ? shiftMonth(1) : view === 'months' ? shiftYear(1) : shiftYearPage(1)
  // Cycle days → months → years; from years, jump back to days.
  const onHeaderClick = () =>
    setView((v) => (v === 'days' ? 'months' : v === 'months' ? 'years' : 'days'))

  const todayDisabled = isDisabledDay(today)

  const popover = open && pos && (
    <div
      id={popoverId}
      ref={popoverRef}
      role='dialog'
      aria-modal='false'
      aria-label={ariaLabel}
      className='wizard-datepicker-popover'
      style={pos}
    >
      <div className='wizard-datepicker-header'>
        <button
          type='button'
          className='wizard-datepicker-nav'
          aria-label={prevLabel}
          onClick={onPrev}
        >
          <ChevronLeft size={16} />
        </button>
        <button
          type='button'
          className='wizard-datepicker-title'
          aria-label={intl.formatMessage({id: 'ONBOARDING_WIZARD.DATEPICKER.CHOOSE_MONTH_YEAR'})}
          onClick={onHeaderClick}
        >
          {headerLabel}
        </button>
        <button
          type='button'
          className='wizard-datepicker-nav'
          aria-label={nextLabel}
          onClick={onNext}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {view === 'days' && renderDays()}
      {view === 'months' && renderMonths()}
      {view === 'years' && renderYears()}

      <div className='wizard-datepicker-footer'>
        <button
          type='button'
          className='wizard-datepicker-action'
          disabled={todayDisabled}
          onClick={() => pick(today)}
        >
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.DATEPICKER.TODAY'})}
        </button>
        <button
          type='button'
          className='wizard-datepicker-action wizard-datepicker-action--muted'
          disabled={!value}
          onClick={() => {
            onChange('')
            setOpen(false)
            triggerRef.current?.focus()
          }}
        >
          {intl.formatMessage({id: 'GENERAL.CLEAR'})}
        </button>
      </div>
    </div>
  )

  return (
    <>
      <button
        id={triggerId}
        ref={triggerRef}
        type='button'
        disabled={disabled}
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-label={ariaLabel}
        className={`wizard-datepicker-trigger${open ? ' is-open' : ''}${displayValue ? '' : ' is-empty'} ${className}`.trim()}
        onClick={() => (open ? setOpen(false) : openPopover())}
        style={style}
      >
        <span className='wizard-datepicker-value'>
          {displayValue ||
            placeholder ||
            intl.formatMessage({id: 'ONBOARDING_WIZARD.DATEPICKER.PLACEHOLDER'})}
        </span>
        <CalendarDays size={16} className='wizard-datepicker-icon' aria-hidden />
      </button>
      {popover && createPortal(popover, document.body)}
    </>
  )
}

export default WizardDatePicker
